"use client";

import { UserType } from "@/app/types/user";
import { Avatar, Box, Button, colors, Divider, Fade, IconButton, ListItemIcon, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@v2/lib/redux/hooks";
import { useAuthSession } from "@v2/hooks/useAuthSession";
import throttle from "lodash.throttle";
import { CircleUserRound, LogOut, ShirtIcon, ShoppingBag, UserRound } from "lucide-react";
import Link from "next/link";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import MediaV2 from "../MediaV2";
import axiosNext from "@/api/axios-next";
import { storeSessionToCsr } from "@/app/store/slices/general";
import { useRouter } from "@bprogress/next/app";
import { numberFormat } from "@v2/helpers/optimizedHelpers";
import { DeviceViewport } from "@v2/types";

interface IProps {
    white?: boolean;
    viewport?: DeviceViewport
}

export default function ProfileButtonProfileButton({ white, viewport }: IProps) {
    const { isReady, isAuthenticated, user } = useAuthSession();

    if (!isReady) return <ProfileSkeleton />;

    return (
        <Fragment>
            {isAuthenticated ? (
                <AccountMenu user={user} viewport={viewport} />
            ) : (
                <LoginButton />
            )}
        </Fragment>
    )
}

export function ProfileSkeleton() {
    return (
        <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: 'grey.200', flexShrink: 0 }} />
    );
}

function AccountMenu({ user, viewport }: {
    user: UserType,
    viewport: DeviceViewport
}) {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const router = useRouter()
    const isMobile = viewport === "mobile"

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [open, setOpen] = useState(Boolean(anchorEl))

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setOpen(true)
    };
    const handleClose = () => {
        setAnchorEl(null);
        setOpen(false)
    };

    const scrollEvent = throttle(() => {
        setOpen(false)
    }, 0)

    useEffect(() => {
        window.addEventListener('scroll', scrollEvent)
        return () => {
            window.removeEventListener('scroll', scrollEvent)
        };
    }, []);

    const onExit = useCallback(() => {
        axiosNext.get('/api/auth-2/sign-out').then((res) => {
            dispatch(storeSessionToCsr(null))
            localStorage.removeItem('accessToken')
            dispatch(storeSessionToCsr(null))
            router.push('/fa/sign-in')
        }).catch(() => {

        })
    }, [])

    return (
        <Fragment>
            {/* The avatar is the user's own photo, or the first letter of their
                name when there is none. The header is on every page, so this
                would end up in every Clarity recording. */}
            <Box data-clarity-mask="True" sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{ height: 42, width: 42 }}
                    aria-label="profile button"
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Avatar sx={{
                        bgcolor: (!user?.avatar && !!user?.first_name?.[0]) ? colors.grey[200] : 'transparent',
                        textTransform: 'capitalize',
                        color: colors.grey[800],
                        height: 42, width: 42,
                        cursor: 'pointer',
                        fontFamily: 'font, font Fallback'
                    }}>
                        {user?.avatar ? (
                            <MediaV2
                                media_id={user?.avatar.id}
                                alt={user.title_name}
                                disablePlaceholder
                                height={64} width={64}
                                imageProps={{
                                    fill: true
                                }} boxProps={{
                                    sx: {
                                        width: 34,
                                        height: 34,
                                        borderRadius: '50%'
                                    }
                                }} />
                        ) : user?.first_name?.[0] ?? (
                            <UserRound size={20} />
                        )}
                    </Avatar>
                </IconButton>
            </Box>
            <Menu
                anchorEl={anchorEl}
                disableScrollLock
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: '4px',
                            mr: isMobile ? 1 : 0,
                            [theme.breakpoints.down('sm')]: {
                                mr: 'unset',
                                ml: 1,
                            },
                            '& .MuiMenuItem-root': {
                                justifyContent: 'space-between',
                                fontSize: 15,
                                minWidth: 170,
                                height: 34,
                                minHeight: 'unset'
                            },
                            '& .MuiListItemIcon-root': {
                                minWidth: 'unset !important',
                                ml: 3
                            },
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                        },
                    },
                }}
                transformOrigin={isMobile
                    ? { horizontal: 'right', vertical: 'top' }
                    : { horizontal: 'left', vertical: 'top' }
                }
                anchorOrigin={isMobile 
                    ? { horizontal: 'right', vertical: 'bottom' }
                    : { horizontal: 'left', vertical: 'bottom' }
                }
            >
                <MenuItem component={Link} href="/fa/v2/profile" prefetch={false}>
                    <span data-clarity-mask="True">
                        {user?.first_name && user?.last_name ? (
                            user?.first_name + " " + user?.last_name
                        ) : (
                            "حساب کاربری"
                        )}
                    </span>
                    <ListItemIcon>
                        <UserRound size={21} />
                    </ListItemIcon>
                </MenuItem>
                <MenuItem component={Link} href="/fa/v2/profile/orders" prefetch={false}>
                    <Fragment>سفارشات</Fragment>
                    <ListItemIcon>
                        <ShoppingBag size={21} />
                    </ListItemIcon>
                </MenuItem>
                {/* <MenuItem component={Link} href="/fa/v2/profile/try_baskets" prefetch={false}>
                    <Fragment>درخواست‌های پرو</Fragment>
                    <ListItemIcon>
                        <ShirtIcon size={21} />
                    </ListItemIcon>
                </MenuItem> */}
                <Divider sx={{my: '4px !important'}} />
                <MenuItem>
                    <Fragment>اعتبار</Fragment>
                    <Typography sx={{
                        fontSize: 15,
                        fontWeight: 500,
                        '& > span': {
                            fontSize: 12,
                            fontWeight: 300
                        }
                    }}>
                        {numberFormat(user?.wallet_irt ?? 0)} <span>تومانء</span>
                    </Typography>
                </MenuItem>
                <Divider sx={{my: '4px !important'}} />


                <MenuItem onClick={onExit}>
                    <Fragment>خروج</Fragment>
                    <ListItemIcon>
                        <LogOut size={21} color={colors.red[800]} />
                    </ListItemIcon>
                </MenuItem>
            </Menu>
        </Fragment>
    );
}

declare module '@mui/material/Button' {
    interface ButtonComponentOverwrite {
        component: true;
    }
}

function LoginButton() {
    return (
        <Fade in={true}>
            <IconButton
                component={Link}
                href="/fa/sign-in"
                prefetch={false}
                sx={{ width: 42, height: 42, color: 'grey.800' }}
                aria-label="ورود به حساب کاربری"
            >
                <CircleUserRound size={23} />
            </IconButton>
        </Fade>
    );
}
